
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import { EmbeddedContentType } from 'client-website-ts-library/types/EmbeddedContent/EmbeddedContent';

import Embeddable from '../Common/Embeddable.vue';

import { ItemCyclerSlide, ItemCyclerSlideType, ItemCyclerImageSlide, ItemCyclerEmbeddableSlide, ItemCyclerReiszableImageSlide } from '../../app_code/ItemCycler';

@Component({
  components: {
    Embeddable,
  },
})
export default class ItemCyclerItem extends Vue {
  @Prop()
  private readonly item!: ItemCyclerSlide;

  @Prop()
  private readonly active!: boolean;

  @Prop({ default: 1200 })
  private readonly size!: number;

  // The video iframe swallows mouse/touch events, so an overlay sits on top of it to keep swiping working.
  // Taps on the overlay are forwarded to the player as play/pause through the YouTube/Vimeo postMessage API.
  // The player's control bar is left uncovered so seeking, volume and fullscreen still work natively.
  private videoUnlocked = false;

  private videoPlaying = false;

  private pointerStartX = 0;

  private pointerStartY = 0;

  private unlockTimeout: number | null = null;

  get embeddableType(): EmbeddedContentType | null {
    return this.item.type === ItemCyclerSlideType.Embeddable ? this.getEmbeddableSlide().embeddable.Type : null;
  }

  mounted(): void {
    const frame = this.getVideoFrame();

    if (frame) {
      window.addEventListener('message', this.handlePlayerMessage);
      frame.addEventListener('load', this.subscribeToPlayer);
    }
  }

  beforeDestroy(): void {
    window.removeEventListener('message', this.handlePlayerMessage);
    this.clearUnlockTimeout();
  }

  @Watch('active')
  handleActiveChanged(active: boolean): void {
    if (!active) {
      this.videoUnlocked = false;
    }
  }

  getVideoFrame(): HTMLIFrameElement | null {
    if (this.embeddableType !== EmbeddedContentType.Youtube && this.embeddableType !== EmbeddedContentType.Vimeo) {
      return null;
    }

    return this.$el.querySelector('iframe');
  }

  postToPlayer(message: object): void {
    const frame = this.getVideoFrame();

    if (frame && frame.contentWindow) {
      frame.contentWindow.postMessage(JSON.stringify(message), '*');
    }
  }

  subscribeToPlayer(): void {
    // Vimeo subscribes once it reports 'ready' (see handlePlayerMessage)
    if (this.embeddableType === EmbeddedContentType.Youtube) {
      this.postToPlayer({ event: 'listening', id: this.item.id, channel: 'widget' });
    }
  }

  handlePlayerMessage(e: MessageEvent): void {
    const frame = this.getVideoFrame();

    if (!frame || e.source !== frame.contentWindow) {
      return;
    }

    let data;

    try {
      data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
    } catch {
      return;
    }

    if (!data) {
      return;
    }

    if (this.embeddableType === EmbeddedContentType.Youtube) {
      // 1 = playing, 3 = buffering
      if (data.event === 'onStateChange') {
        this.setVideoPlaying(data.info === 1 || data.info === 3);
      } else if (data.event === 'infoDelivery' && data.info && typeof data.info.playerState === 'number') {
        this.setVideoPlaying(data.info.playerState === 1 || data.info.playerState === 3);
      }
    } else if (data.event === 'ready') {
      ['play', 'pause', 'ended'].forEach((value) => this.postToPlayer({ method: 'addEventListener', value }));
    } else if (data.event === 'play') {
      this.setVideoPlaying(true);
    } else if (data.event === 'pause' || data.event === 'ended') {
      this.setVideoPlaying(false);
    }
  }

  setVideoPlaying(playing: boolean): void {
    this.videoPlaying = playing;

    if (playing) {
      this.clearUnlockTimeout();
    }
  }

  clearUnlockTimeout(): void {
    if (this.unlockTimeout !== null) {
      window.clearTimeout(this.unlockTimeout);
      this.unlockTimeout = null;
    }
  }

  handlePointerDown(e: MouseEvent | TouchEvent): void {
    const point = 'touches' in e ? e.touches[0] : e;

    this.pointerStartX = point.clientX;
    this.pointerStartY = point.clientY;
  }

  handleOverlayClick(e: MouseEvent): void {
    // Ignore the click that ends a swipe
    if (Math.abs(e.clientX - this.pointerStartX) >= 10 || Math.abs(e.clientY - this.pointerStartY) >= 10) {
      return;
    }

    const play = !this.videoPlaying;

    if (this.embeddableType === EmbeddedContentType.Youtube) {
      this.postToPlayer({ event: 'command', func: play ? 'playVideo' : 'pauseVideo', args: [] });
    } else {
      this.postToPlayer({ method: play ? 'play' : 'pause' });
    }

    if (play) {
      // Some browsers (mainly iOS) refuse scripted playback before the user has touched the player itself.
      // If it hasn't started shortly, get out of the way so the next tap goes straight to the player.
      this.clearUnlockTimeout();
      this.unlockTimeout = window.setTimeout(() => {
        this.unlockTimeout = null;

        if (!this.videoPlaying) {
          this.videoUnlocked = true;
        }
      }, 1500);
    }
  }

  getEmbeddableSlide(): ItemCyclerEmbeddableSlide {
    return this.item as ItemCyclerEmbeddableSlide;
  }

  getImageSlide(): ItemCyclerImageSlide {
    return this.item as ItemCyclerImageSlide;
  }

  getResizableImageSlide(): ItemCyclerReiszableImageSlide {
    return this.item as ItemCyclerReiszableImageSlide;
  }
}
